import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  IconButton,
  ListSubheader,
  Tooltip,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import {
  LabelOrigin,
  LabelSuggestionDetails,
  LabelType,
  LabelValue,
  LabelValueDetails,
  LabelValueSource,
  SuggestionLabelValue,
  TimeSignal
} from 'graphql/types';
import { usePopoverState } from 'modules/common/hooks/usePopoverState';
import {
  determineLabelAcceptedBy,
  isLabelValueEmpty
} from 'modules/timesheet-labels/hooks/utils';
import { useFeatureFlag } from 'modules/common/hooks';
import { useMessage } from 'modules/intl';
import { useMeContext } from '../../../me';
import { useNotesFilterContext } from '../../NotesFilterContextProvider';
import { formatTimeFromIsoString } from '../../../../utils/date-time';
import { NotesListGroupHeaderCheckbox } from '../NotesListGroupHeaderCheckbox';
import { ListLabels } from '../ListLabels';
import { NotesListGroupDuration } from '../NotesListGroupDuration';
import { NoteSourceGroup } from '../NoteSourceGroup';
import { CustomLabelsList } from '../../../customLabels/components/CustomLabelsList';
import { TimesheetLabel } from '../../../timesheet-labels/labelSchema';
import { useIsSmartSuggestionSettingEnabled } from '../../../timesheet-labels/hooks';
import { useNotesAnimationContext } from '../../providers';
import { useAnimationStyles } from '../NoteListItem';
import { ExcludedTag } from '../ExcludedTag';
import { useGroupClassification } from './hooks/useGroupClassification';
import { GroupClassificationTag } from './GroupClassificationTag';
import { useGroupTimePeriod } from './hooks';
import { NotesListLabelGroupQuickActions } from './NotesListLabelGroupQuickActions';

type NotesListLabelGroupHeaderType = {
  groupKey: string;
  onExpandCollapse: (day: string) => void;
  expanded: boolean;
  notes: TimeSignal[];
  primaryNote: TimeSignal;
  totalCount: number;
  totalDuration?: number;
  isUncategorizedGroup: boolean;
  isDragHovering: boolean;
  onDialogOpen: () => void;
};

interface UseStylesProps {
  isUncategorizedGroup: boolean;
  expanded: boolean;
  isDragHovering: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: ({
      isUncategorizedGroup,
      isDragHovering
    }: UseStylesProps) =>
      isDragHovering
        ? theme.palette.success.light
        : !isUncategorizedGroup
        ? theme.palette.groupBy.labelGroupHeaderBackground
        : 'inherit',
    margin: theme.spacing(0),
    padding: ({ isUncategorizedGroup }: UseStylesProps) =>
      isUncategorizedGroup ? theme.spacing(0) : theme.spacing(0, 0, 0, 1),
    height: theme.spacing(7.5),
    width: '100%',
    cursor: 'pointer',
    boxShadow: ({ isUncategorizedGroup }: UseStylesProps) =>
      isUncategorizedGroup
        ? undefined
        : 'inset 0 -1px 0 0 rgb(100 121 143 / 12%)',
    borderRadius: ({ isUncategorizedGroup, isDragHovering }: UseStylesProps) =>
      isDragHovering && !isUncategorizedGroup
        ? theme.spacing(0.5, 0.5, 0, 0)
        : theme.spacing(0, 0, 0, 0),
    '&:hover, &:focus': {
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.shadows[2]
    },
    '&:focus': {
      outline: theme.spacing(0)
    }
  },
  container: {
    height: '100%'
  },
  icon: {
    padding: theme.spacing(0.25),
    marginRight: theme.spacing(1.125)
  },
  iconColor: {
    color: theme.palette.text.primary
  },
  checkboxAndIcon: {
    display: 'flex',
    alignItems: 'center'
  },
  time: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    color: theme.palette.text.primary,
    width: theme.spacing(16),
    margin: theme.spacing(0, 0.75, 0, 2),
    flexShrink: 0
  },
  gridRoot: {
    padding: theme.spacing(0.5, 0, 0.5, 0),
    margin: ({ isUncategorizedGroup }: UseStylesProps) =>
      theme.spacing(0, isUncategorizedGroup ? 5.25 : 6.25, 0, 0),
    width: '100%',
    userSelect: 'none',
    boxShadow: ({ isUncategorizedGroup, expanded }: UseStylesProps) =>
      isUncategorizedGroup && expanded
        ? 'inset 0 -1px 0 0 rgb(100 121 143 / 12%)'
        : 'inset 0 0 0 0 rgb(100 121 143 / 12%)'
  },
  source: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1.25)
  },
  timeContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    flexGrow: '1'
  },
  description: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flexGrow: '1',
    width: '100%',
    position: ({ isUncategorizedGroup }: UseStylesProps) =>
      isUncategorizedGroup ? 'relative' : undefined,
    top: ({ isUncategorizedGroup }: UseStylesProps) =>
      isUncategorizedGroup ? theme.spacing(2) : undefined
  },
  descriptionContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  durationRight: {
    display: 'flex',
    alignItems: 'center'
  },
  contentRoot: {
    width: '100%'
  },
  box: {
    width: '100%',
    position: 'relative'
  }
}));

const useCustomTagRootStyles = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    justifyContent: 'flex-end',
    margin: theme.spacing(0)
  }
}));

export const useTimesheetLabels = (note: TimeSignal): TimesheetLabel[] => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  return useMemo(() => {
    return note?.labelValues
      ?.filter(
        (l: LabelValueDetails) =>
          l.label?.origin !== LabelOrigin.User &&
          l.label?.type !== LabelType.Comment
      )
      .map((l: LabelValueDetails) => {
        const retVal: TimesheetLabel = {
          ...l.label,
          value: l.value
        };
        if (isSmartSuggestionEnabled) {
          retVal.valueSource = l.valueSource;

          if (isTimeSignalFederatedSuggestionsEnabled) {
            const suggestion = note.suggestions.suggestions.find(
              (suggestion: LabelSuggestionDetails) =>
                suggestion.label.id === l.label.id
            );
            retVal.suggestedValues = suggestion?.suggestedValues.map(
              (suggestedValue: SuggestionLabelValue | undefined | null) =>
                (suggestedValue as LabelValue) ?? null
            );
          }
        }
        retVal.acceptedBy = determineLabelAcceptedBy(
          retVal.valueSource ?? LabelValueSource.User,
          l.acceptedBy,
          isSmartSuggestionEnabled
        );
        return retVal;
      });
  }, [
    note.suggestions.suggestions,
    isSmartSuggestionEnabled,
    isTimeSignalFederatedSuggestionsEnabled,
    note?.labelValues
  ]);
};
const useCustomLabels = (note: TimeSignal) => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  return useMemo(
    () =>
      note.labelValues
        ?.filter((l) => l.label?.origin === LabelOrigin.User)
        .map((l) => {
          const retVal: TimesheetLabel = {
            ...l.label,
            value: l.value
          };
          if (isSmartSuggestionEnabled) {
            retVal.valueSource = l.valueSource;

            if (isTimeSignalFederatedSuggestionsEnabled) {
              const suggestion = note.suggestions.suggestions.find(
                (suggestion: LabelSuggestionDetails) =>
                  suggestion.label.id === l.label.id
              );
              retVal.suggestedValues = suggestion?.suggestedValues.map(
                (suggestedValue: SuggestionLabelValue | undefined | null) =>
                  (suggestedValue as LabelValue) ?? null
              );
              retVal.suggestionMetadata =
                suggestion?.suggestionMetadata ?? undefined;
              retVal.thresholdMet = suggestion?.thresholdMet;
            }
          }
          retVal.acceptedBy = determineLabelAcceptedBy(
            retVal.valueSource ?? LabelValueSource.User,
            l.acceptedBy,
            isSmartSuggestionEnabled
          );
          return retVal;
        })
        .filter(
          (l) =>
            (isEmptyLabelsEnabled ? !isLabelValueEmpty(l.value) : l.value) ||
            (isEmptyLabelsEnabled &&
              !isLabelValueEmpty(l.suggestedValues?.[0])) ||
            (isTimeSignalFederatedSuggestionsEnabled &&
              !isEmptyLabelsEnabled &&
              l.suggestedValues?.length)
        ),
    [
      note.suggestions.suggestions,
      isSmartSuggestionEnabled,
      isTimeSignalFederatedSuggestionsEnabled,
      isEmptyLabelsEnabled,
      note.labelValues
    ]
  );
};

export const NotesListLabelGroupHeader: React.FC<NotesListLabelGroupHeaderType> = ({
  groupKey,
  onExpandCollapse,
  expanded,
  notes,
  primaryNote,
  totalCount,
  totalDuration,
  isUncategorizedGroup,
  isDragHovering,
  onDialogOpen
}: NotesListLabelGroupHeaderType) => {
  const classes = useStyles({
    isUncategorizedGroup: isUncategorizedGroup,
    expanded: expanded,
    isDragHovering: isDragHovering
  });
  const classesCustomTagRoot = useCustomTagRootStyles();

  const onClick = useCallback(() => {
    onExpandCollapse(groupKey);
  }, [groupKey, onExpandCollapse]);
  const { groupStartTime, groupEndTime } = useGroupTimePeriod(notes);
  const { groupClassification } = useGroupClassification(notes);
  const timesheetLabels = useTimesheetLabels(primaryNote);
  const customLabels = useCustomLabels(primaryNote);

  const me = useMeContext();
  const {
    filterContext: { selectedClassification }
  } = useNotesFilterContext();

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const { collapseIcon, expandIcon } = useMemo(
    () => ({
      collapseIcon: (
        <Tooltip title={<FormattedMessage id="collapse" />}>
          <IconButton
            className={classes.icon}
            data-testid="expand-less-icon"
            size="large"
            aria-label={isTiwScreenReaderEnabled ? 'collapse' : ''}
          >
            <ExpandLessIcon className={classes.iconColor} />
          </IconButton>
        </Tooltip>
      ),
      expandIcon: (
        <Tooltip title={<FormattedMessage id="expand" />}>
          <IconButton
            className={classes.icon}
            data-testid="expand-more-icon"
            size="large"
            aria-label={isTiwScreenReaderEnabled ? 'expand' : ''}
          >
            <ExpandMoreIcon className={classes.iconColor} />
          </IconButton>
        </Tooltip>
      )
    }),
    [classes.icon, classes.iconColor, isTiwScreenReaderEnabled]
  );

  const animationStyles = useAnimationStyles();
  const { hasAnimation } = useNotesAnimationContext();
  const animationData = useMemo(() => {
    const animatedNote = notes.find((note: TimeSignal) =>
      Boolean(hasAnimation(note.id))
    );
    return animatedNote ? hasAnimation(animatedNote.id) : undefined;
  }, [hasAnimation, notes]);

  const {
    anchorEl,
    isPopoverOpen,
    onPopoverOpen,
    onPopoverClose
  } = usePopoverState();

  const gridSizes = useMemo(() => {
    if (customLabels.length) {
      return {
        xs: { tl: 6, cl: 6 },
        sm: { tl: 6, cl: 6 },
        md: { tl: 7, cl: 5 },
        lg: { tl: 8, cl: 4 }
      };
    }
    return {
      xs: { tl: 12, cl: 0 },
      sm: { tl: 12, cl: 0 },
      md: { tl: 12, cl: 0 },
      lg: { tl: 12, cl: 0 }
    };
  }, [customLabels.length]);

  const selectGroupHeaderMsg = useMessage('note.groupBy.selectAllHeader');

  return (
    <ListSubheader
      data-testid={`notes-list-label-group-header-${groupKey}`}
      className={classnames(
        classes.root,
        animationData?.folder && animationStyles[animationData?.folder]
      )}
      onClick={onClick}
      disableSticky={true}
      disableGutters={true}
      onMouseOver={onPopoverOpen}
      onMouseLeave={onPopoverClose}
      tabIndex={0}
    >
      <NotesListLabelGroupQuickActions
        groupKey={groupKey}
        notes={notes}
        onPopoverOpen={onPopoverOpen}
        onPopoverClose={onPopoverClose}
        anchorEl={anchorEl}
        isPopoverOpen={isPopoverOpen}
        openGroupEditDialog={onDialogOpen}
        isUncategorizedGroup={isUncategorizedGroup}
      />
      <Box className={classes.box}>
        <Grid container className={classes.container} wrap="nowrap">
          <Grid item className={classes.checkboxAndIcon}>
            <NotesListGroupHeaderCheckbox
              groupKey={groupKey}
              notes={notes}
              totalCount={totalCount}
              ariaLabelMsg={selectGroupHeaderMsg}
            />
            {expanded ? collapseIcon : expandIcon}
          </Grid>
          <Grid
            container
            wrap="nowrap"
            zeroMinWidth
            className={classnames(classes.gridRoot)}
            item
          >
            <Grid item className={classes.source}>
              <NoteSourceGroup sources={primaryNote.sources} />
            </Grid>
            <Grid item zeroMinWidth className={classes.contentRoot}>
              <Grid container wrap="nowrap" justifyContent="space-between">
                <Grid
                  className={classnames(classes.descriptionContainer)}
                  item
                  lg={7}
                  md={7}
                  sm={7}
                  xs={7}
                >
                  <Typography
                    data-testid={'notes-list-label-group-header-note'}
                    variant="body2"
                    className={classnames(classes.description)}
                  >
                    {!isUncategorizedGroup
                      ? primaryNote.description
                      : primaryNote.description}
                  </Typography>
                </Grid>
                <Grid
                  item
                  lg={5}
                  md={5}
                  sm={5}
                  xs={5}
                  className={classnames(
                    classes.timeContainer,
                    classes.durationRight
                  )}
                  data-testid={'notes-list-label-group-header-timeContainer'}
                >
                  {!isUncategorizedGroup && primaryNote.excluded && (
                    <ExcludedTag
                      note={primaryNote}
                      isItemGrouped={true}
                    ></ExcludedTag>
                  )}
                  {selectedClassification === 'All' && groupClassification && (
                    <GroupClassificationTag
                      groupKey={groupKey}
                      groupClassification={groupClassification}
                    />
                  )}
                  <Typography
                    data-testid={
                      'notes-list-label-group-header-start-and-end-time'
                    }
                    className={classes.time}
                    variant="caption"
                    noWrap
                  >
                    {groupStartTime && groupEndTime && (
                      <>
                        {formatTimeFromIsoString(groupStartTime, me)}
                        <>&nbsp; &ndash; &nbsp;</>
                        {formatTimeFromIsoString(groupEndTime, me)}
                      </>
                    )}
                  </Typography>
                  <NotesListGroupDuration
                    notes={notes}
                    groupKey={groupKey}
                    totalDuration={totalDuration}
                    isLabelGroupDuration={true}
                  />
                </Grid>
              </Grid>
              <Grid container wrap="nowrap" justifyContent="space-between">
                <Grid item xs={12} md={12}>
                  <Grid container wrap="nowrap">
                    {!isUncategorizedGroup && (
                      <Grid
                        container
                        xs={gridSizes.xs.tl}
                        sm={gridSizes.sm.tl}
                        md={gridSizes.md.tl}
                        lg={gridSizes.lg.tl}
                      >
                        <ListLabels labels={timesheetLabels} />
                      </Grid>
                    )}

                    {!isUncategorizedGroup && Boolean(customLabels.length) && (
                      <Grid
                        container
                        xs={gridSizes.xs.cl}
                        sm={gridSizes.sm.cl}
                        md={gridSizes.md.cl}
                        lg={gridSizes.lg.cl}
                        justifyContent="flex-end"
                      >
                        <CustomLabelsList
                          labels={customLabels}
                          classes={classesCustomTagRoot}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </ListSubheader>
  );
};

export default React.memo(NotesListLabelGroupHeader);
